import {
  Component,
  lazy,
  Suspense,
  useEffect,
  type ComponentType,
  type LazyExoticComponent,
  type ReactNode,
} from "react";
import { Routes, Route, useLocation, matchPath } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { LoadingScreen, ContentLoading } from "@/components/LoadingScreen";
const Index = lazy_com_recuperacao(() => import("@/pages/Index"), "index");

const CHAVE_RECARGA_IMPORTACAO = "recuperar-importacao-dinamica:";

function eh_erro_importacao_dinamica(erro: unknown): boolean {
  if (!(erro instanceof Error)) return false;

  const mensagem = erro.message.toLowerCase();

  return (
    mensagem.includes("failed to fetch dynamically imported module") ||
    mensagem.includes("error loading dynamically imported module") ||
    mensagem.includes("importing a module script failed")
  );
}

function lazy_com_recuperacao<T extends ComponentType<unknown>>(
  carregar_modulo: () => Promise<{ default: T }>,
  chave_rota: string,
): LazyExoticComponent<T> {
  return lazy(async () => {
    const chave_recarga = `${CHAVE_RECARGA_IMPORTACAO}${chave_rota}`;
    const ja_recarregou =
      typeof window !== "undefined" && sessionStorage.getItem(chave_recarga) === "1";

    try {
      const modulo = await carregar_modulo();

      if (typeof window !== "undefined") {
        sessionStorage.removeItem(chave_recarga);
      }

      return modulo;
    } catch (erro) {
      if (
        typeof window !== "undefined" &&
        eh_erro_importacao_dinamica(erro) &&
        !ja_recarregou
      ) {
        sessionStorage.setItem(chave_recarga, "1");
        window.location.reload();

        return new Promise<{ default: T }>(() => undefined);
      }

      if (typeof window !== "undefined") {
        sessionStorage.removeItem(chave_recarga);
      }

      throw erro;
    }
  });
}

const Auth = lazy_com_recuperacao(() => import("@/pages/Auth"), "auth");
const Dashboard = lazy_com_recuperacao(() => import("@/pages/Dashboard"), "dashboard");
const Editor = lazy_com_recuperacao(() => import("@/pages/Editor"), "editor");
const ResetPassword = lazy_com_recuperacao(
  () => import("@/pages/ResetPassword"),
  "reset-password",
);
const NotFound = lazy_com_recuperacao(() => import("@/pages/NotFound"), "not-found");
const Settings = lazy_com_recuperacao(() => import("@/pages/Settings"), "settings");
const Unsubscribe = lazy_com_recuperacao(
  () => import("@/pages/Unsubscribe").then((modulo) => ({ default: modulo.Unsubscribe })),
  "unsubscribe",
);
const Changelog = lazy_com_recuperacao(() => import("@/pages/Changelog"), "changelog");

const pageVariants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
};

const pageTransition = {
  duration: 0.15,
  ease: "easeOut" as const,
};

function AnimatedPage({ children }: { children: ReactNode }) {
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={pageTransition}
      style={{ minHeight: "100vh" }}
    >
      {children}
    </motion.div>
  );
}

class RouteErrorBoundary extends Component<
  { children: ReactNode },
  { has_error: boolean; error_message: string }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { has_error: false, error_message: "" };
  }

  static getDerivedStateFromError(error: unknown) {
    return {
      has_error: true,
      error_message:
        error instanceof Error ? error.message : "Não foi possível carregar a página.",
    };
  }

  handle_retry = () => {
    this.setState({ has_error: false, error_message: "" });
    window.location.reload();
  };

  render() {
    if (this.state.has_error) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center text-foreground">
          <h1 className="text-xl font-semibold">A página não carregou corretamente</h1>
          <p className="max-w-md text-sm text-muted-foreground">
            {this.state.error_message}
          </p>
          <Button onClick={this.handle_retry}>Tentar novamente</Button>
        </div>
      );
    }

    return this.props.children;
  }
}

/** Rotas que usam o DashboardLayout compartilhado */
const NAV_ROUTES = [
  "/dashboard",
  "/settings",
];

function is_nav_route(pathname: string): boolean {
  return NAV_ROUTES.some((r) => matchPath(r, pathname));
}

/**
 * DashboardLayout importado estaticamente: sidebar/dock/topbar nunca desmontam.
 * Suspense envolve apenas o conteudo interno das rotas.
 */
function NavRoutes() {
  const location = useLocation();

  return (
    <DashboardLayout>
      <RouteErrorBoundary>
        <Suspense fallback={<ContentLoading />}>
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.08, ease: "easeOut" }}
            >
              <Routes location={location}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/settings" element={<Settings />} />
              </Routes>
            </motion.div>
          </AnimatePresence>
        </Suspense>
      </RouteErrorBoundary>
    </DashboardLayout>
  );
}

export function AnimatedRoutes() {
  const location = useLocation();

  // Limpa toasts pendentes ao trocar de rota
  useEffect(() => {
    toast.dismiss();
  }, [location.pathname]);
  /* Rotas internas: layout persistente com nav fixa */
  if (is_nav_route(location.pathname)) {
    return <NavRoutes />;
  }

  /* Rotas publicas / editor: animacao completa */
  return (
    <RouteErrorBoundary>
      <Suspense fallback={<LoadingScreen />}>
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<AnimatedPage><Index /></AnimatedPage>} />
            <Route path="/auth" element={<AnimatedPage><Auth /></AnimatedPage>} />
            <Route path="/reset-password" element={<AnimatedPage><ResetPassword /></AnimatedPage>} />
            <Route path="/unsubscribe" element={<AnimatedPage><Unsubscribe /></AnimatedPage>} />
            <Route path="/editor/:projectId" element={<AnimatedPage><Editor /></AnimatedPage>} />
            <Route path="/changelog" element={<Changelog />} />
            <Route path="*" element={<AnimatedPage><NotFound /></AnimatedPage>} />
          </Routes>
        </AnimatePresence>
      </Suspense>
    </RouteErrorBoundary>
  );
}
